import { TFunction } from 'i18next';
import { TwTheme } from '../../Tw';

// Constants
export const ABC_CATEGORIES = ['A+', 'A', 'B', 'C'] as const;
export const XYZ_CATEGORIES = ['X', 'Y', 'Z'] as const;
export const COMBINED_CATEGORIES = [
  'Strategic',
  'Critical',
  'Regular',
  'Review',
  'Non-critical',
  'Dead-Stock',
] as const;

export interface SKUData {
  xyz_rank: XYZCategory;
  combined_rank: CombinedCategory;
  accuracy: any;
  average_lines_per_day(
    average_lines_per_day: any,
  ): import('react').ReactNode | Iterable<import('react').ReactNode>;
  average_quantity_per_day(
    average_quantity_per_day: any,
  ): import('react').ReactNode | Iterable<import('react').ReactNode>;
  lines_accuracy: number;
  quantity_accuracy: number;
  abc_accuracy: number;
  xyz_accuracy: number;
  sku: string;
  order_line_count: number;
  quantity_ordered: number;
  rank: string;
  total_days_ordered: number;
  total_weeks_ordered: number;
  avg_daily_lines_count: number;
  avg_weekly_lines_count: number;
  min_daily_lines_count: number;
  min_weekly_lines_count: number;
  max_daily_lines_count: number;
  max_weekly_lines_count: number;
  max_lines: number;
  min_lines: number;
  max_quantity: number;
  min_quantity: number;
  [key: `w${string}_lines`]: number;
  [key: `w${string}_quantity`]: number;
  [key: `w${string}_fc_lines`]: number;
  [key: `w${string}_fc_quantity`]: number;
  [key: `w${string}_abc`]: string;
  [key: `w${string}_fc_abc`]: string;
}

interface AccuracyMetrics {
  linesAccuracy: number;
  quantityAccuracy: number;
  abcAccuracy: number;
}

export type ABCCategory = (typeof ABC_CATEGORIES)[number];
export type XYZCategory = (typeof XYZ_CATEGORIES)[number];
export type CombinedCategory = (typeof COMBINED_CATEGORIES)[number];

const categoryABCXYZ = TwTheme.extend.colors.categoryABCXYZ;

// Colors from theme
export const ABC_COLORS: Record<ABCCategory | 'Unknown', string> = {
  'A+': categoryABCXYZ.S,
  A: categoryABCXYZ.A,
  B: categoryABCXYZ.B,
  C: categoryABCXYZ.C,
  Unknown: categoryABCXYZ.unknown,
};

export const XYZ_COLORS: Record<XYZCategory, string> = {
  X: categoryABCXYZ.X,
  Y: categoryABCXYZ.Y,
  Z: categoryABCXYZ.Z,
};

export const ABC_XYZ_COLORS: Record<CombinedCategory, string> = {
  Strategic: categoryABCXYZ.strategic,
  Critical: categoryABCXYZ.critical,
  Regular: categoryABCXYZ.regular,
  Review: categoryABCXYZ.review,
  'Non-critical': categoryABCXYZ.nonCritical,
  'Dead-Stock': categoryABCXYZ.deadStock,
};

// Descriptions (translatable via TFunction)
export const getABCCategoryDescriptions = (
  t: TFunction<'app'>,
): Record<ABCCategory | 'Unknown', string> => ({
  'A+': t('Very high moving item', { ns: 'app' }),
  A: t('High moving item', { ns: 'app' }),
  B: t('Medium moving item', { ns: 'app' }),
  C: t('Low moving item', { ns: 'app' }),
  Unknown: t('Unclassified item', { ns: 'app' }),
});

export const getXYZCategoryDescriptions = (
  t: TFunction<'app'>,
): Record<XYZCategory, string> => ({
  X: t('Stable demand', { ns: 'app' }),
  Y: t('Variable demand', { ns: 'app' }),
  Z: t('Highly variable demand', { ns: 'app' }),
});

export const getABCXYZMatrix = (t: TFunction<'app'>) => ({
  'A+': {
    X: t('Strategic', { ns: 'app' }),
    Y: t('Critical', { ns: 'app' }),
    Z: t('Review', { ns: 'app' }),
  },
  A: {
    X: t('Strategic', { ns: 'app' }),
    Y: t('Critical', { ns: 'app' }),
    Z: t('Review', { ns: 'app' }),
  },
  B: {
    X: t('Regular', { ns: 'app' }),
    Y: t('Regular', { ns: 'app' }),
    Z: t('Review', { ns: 'app' }),
  },
  C: {
    X: t('Regular', { ns: 'app' }),
    Y: t('Review', { ns: 'app' }),
    Z: t('Non-critical', { ns: 'app' }),
  },
  D: {
    X: t('Dead-Stock', { ns: 'app' }),
    Y: t('Dead-Stock', { ns: 'app' }),
    Z: t('Dead-Stock', { ns: 'app' }),
  },
});

export const ABC_WEIGHTS = {
  'A+': 5,
  A: 4,
  B: 3,
  C: 2,
  //   D: 1,
  Unknown: 0,
};

// Classification functions
export function getWeeksFromData(sku: SKUData): number[] {
  return Object.keys(sku)
    .filter(key => key.startsWith('w') && key.includes('_lines'))
    .map(key => parseInt(key.split('_')[0].substring(1)))
    .sort((a, b) => a - b);
}

export const getXYZClassification = (
  data: SKUData,
  week: string | number | number[],
  forecast = false,
): string => {
  // Convert all inputs to numbers
  const weeks = Array.isArray(week) ? week.map(w => Number(w)) : [Number(week)];

  const prefix = forecast ? 'fc_' : '';

  // Get the order lines for each week
  const values = weeks.map(w => Number(data[`w${w}_${prefix}lines`]));

  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance =
    values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / values.length;
  const cv = Math.sqrt(variance) / mean;

  if (cv <= 0.5) return 'X';
  if (cv <= 1) return 'Y';
  return 'Z';
};

export function getABCXYZCategory(
  t: TFunction<'app'>,
  abc: string,
  xyz: string,
): string {
  const matrix: Record<string, Record<string, string>> = {
    'A+': {
      X: t('Strategic', { ns: 'app' }),
      Y: t('Critical', { ns: 'app' }),
      Z: t('Review', { ns: 'app' }),
    },
    A: {
      X: t('Strategic', { ns: 'app' }),
      Y: t('Critical', { ns: 'app' }),
      Z: t('Review', { ns: 'app' }),
    },
    B: {
      X: t('Regular', { ns: 'app' }),
      Y: t('Regular', { ns: 'app' }),
      Z: t('Review', { ns: 'app' }),
    },
    C: {
      X: t('Regular', { ns: 'app' }),
      Y: t('Review', { ns: 'app' }),
      Z: t('Non-critical', { ns: 'app' }),
    },
  };

  return matrix[abc]?.[xyz] || t('Review', { ns: 'app' });
}

export function getCombinedDescription(
  t: TFunction<'app'>,
  abc: string,
  xyz: string,
): string {
  const abcDesc = getABCCategoryDescriptions(t)[abc as ABCCategory] || '';
  const xyzDesc = getXYZCategoryDescriptions(t)[xyz as XYZCategory] || '';
  return t('{{abcDesc}} with {{xyzDesc}}', { abcDesc, xyzDesc, ns: 'app' });
}

// Helper functions
export function calculateVariability(values: number[]): number {
  if (values.length === 0) return 0;
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const variance =
    values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
    values.length;
  return Math.sqrt(variance) / mean; // Coefficient of Variation
}
export function getSKUMetrics(sku: SKUData, t: TFunction<'app'>) {
  const weeks = getWeeksFromData(sku);
  const xyz = getXYZClassification(sku, weeks.map(Number));
  const category = getABCXYZCategory(t, sku.rank, xyz);

  return {
    abc: sku.rank,
    xyz,
    category,
    description: getCombinedDescription(t, sku.rank, xyz),
  };
}
